/**
 * Custom fetch mutator wired into orval's generated React Query hooks.
 *
 * Responsibilities:
 *   - inject Bearer JWT from auth store
 *   - inject X-Tenant-Slug from tenant context (subdomain detection)
 *   - bubble 401 → auth refresh, 403 → capability error
 *
 * Read by orval at generation time (see orval.config.ts). Safe to import
 * standalone for manual API calls before generated hooks exist.
 */
export interface FetchOptions extends Omit<RequestInit, 'body'> {
  url: string;
  params?: Record<string, string | number | boolean | undefined>;
  data?: unknown;
  baseURL?: string;
}

export interface ApiError extends Error {
  status: number;
  body: unknown;
}

let authTokenProvider: () => string | null = () => null;
let tenantSlugProvider: () => string | null = () => null;

export function setAuthTokenProvider(fn: () => string | null): void {
  authTokenProvider = fn;
}
export function setTenantSlugProvider(fn: () => string | null): void {
  tenantSlugProvider = fn;
}

export async function customFetch<T>(opts: FetchOptions): Promise<T> {
  const { url, params, data, baseURL = '/api/v1', headers, ...rest } = opts;

  const qs = params
    ? '?' +
      new URLSearchParams(
        Object.entries(params).flatMap(([k, v]) =>
          v === undefined ? [] : [[k, String(v)]],
        ),
      ).toString()
    : '';

  const finalHeaders = new Headers(headers ?? {});
  finalHeaders.set('Accept', 'application/json');
  if (data !== undefined && !finalHeaders.has('Content-Type')) {
    finalHeaders.set('Content-Type', 'application/json');
  }
  const token = authTokenProvider();
  if (token) finalHeaders.set('Authorization', `Bearer ${token}`);
  const slug = tenantSlugProvider();
  if (slug) finalHeaders.set('X-Tenant-Slug', slug);

  const init: RequestInit = { ...rest, headers: finalHeaders };
  if (data !== undefined) init.body = JSON.stringify(data);

  const res = await fetch(`${baseURL}${url}${qs}`, init);

  const ct = res.headers.get('content-type') ?? '';
  const body: unknown = ct.includes('application/json')
    ? await res.json()
    : await res.text();

  if (!res.ok) {
    const err = new Error(`API ${res.status} ${res.statusText}`) as ApiError;
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body as T;
}
